import { useState, useEffect, useCallback } from 'react';
import { Channel, CreateChannelRequest, UpdateChannelRequest } from '../types';
import { channelsApi } from '../services/communicationsApi';

export const useChannels = (options?: { enabled?: boolean }) => {
  const enabled = options?.enabled ?? true;
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchChannels = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await channelsApi.getAll();
      setChannels(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch channels');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    fetchChannels();
  }, [fetchChannels, enabled]);

  const createChannel = useCallback(async (data: CreateChannelRequest): Promise<Channel> => {
    try {
      const channel = await channelsApi.create(data);
      await fetchChannels();
      return channel;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to create channel';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchChannels]);

  const updateChannel = useCallback(async (id: number, data: UpdateChannelRequest): Promise<Channel> => {
    try {
      const channel = await channelsApi.update(id, data);
      await fetchChannels();
      return channel;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update channel';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchChannels]);

  const deleteChannel = useCallback(async (id: number): Promise<void> => {
    try {
      await channelsApi.delete(id);
      await fetchChannels();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete channel';
      setError(msg);
      throw new Error(msg);
    }
  }, [fetchChannels]);

  return {
    channels,
    loading,
    error,
    fetchChannels,
    createChannel,
    updateChannel,
    deleteChannel
  };
};
